/**
 * Location autocomplete for personal events.
 *
 * Photon rather than Google Places: it needs no key and no billing account, so
 * it works the day this ships rather than the day someone finishes a Cloud
 * console. It is also the only free option whose terms permit this — Nominatim,
 * which this project already uses to geocode venues at build time, explicitly
 * forbids per-keystroke queries, and pointing autocomplete at it would be
 * abusing a volunteer-run service.
 *
 * The trade is coverage of business names. Photon reads OpenStreetMap, so a
 * street address resolves well and a venue like "The Nest Climate Campus" may
 * not. The field therefore accepts free text as well: what the user typed is
 * kept whether or not a suggestion was chosen, and a suggestion additionally
 * supplies coordinates, which is what lets travel time be calculated to it.
 */

const ENDPOINT = 'https://photon.komoot.io/api/';

/**
 * The tri-state area, as a bounding box: [minLon, minLat, maxLon, maxLat].
 *
 * Wide enough to cover New York, New Jersey and Connecticut, and tight enough
 * that "Springfield" does not offer Missouri. Photon filters on this rather
 * than merely ranking by it, so nothing outside can be returned.
 */
const TRI_STATE_BBOX = [-75.6, 38.8, -71.5, 42.1] as const;

/** Bias toward Manhattan, so bare street numbers rank sensibly. */
const BIAS = { lat: 40.74, lon: -73.99 };

export interface PlaceSuggestion {
  /** What goes in the field, and what is stored as the venue. */
  label: string;
  /** Shown under the label: the rest of the address. */
  detail: string;
  coords: [number, number];
}

interface PhotonFeature {
  geometry?: { coordinates?: [number, number] };
  properties?: Record<string, string | undefined>;
}

/** "125 Maiden Lane" — a name, or a house number and street. */
function labelFor(p: Record<string, string | undefined>): string {
  if (p.name) return p.name;
  const street = [p.housenumber, p.street].filter(Boolean).join(' ');
  return street || p.city || '';
}

function detailFor(p: Record<string, string | undefined>): string {
  const street = [p.housenumber, p.street].filter(Boolean).join(' ');
  // Only repeat the street when the label was a name rather than the street.
  const parts = [p.name ? street : '', p.city, p.state, p.postcode];
  return parts.filter(Boolean).join(', ');
}

/**
 * Suggestions for what has been typed so far.
 *
 * Takes an AbortSignal because these are fired per keystroke: a slow response
 * to "5 Mad" must not overwrite a fast one to "5 Madison Ave".
 */
export async function searchPlaces(
  query: string,
  signal?: AbortSignal,
): Promise<PlaceSuggestion[]> {
  const q = query.trim();
  if (q.length < 3) return [];

  const url = new URL(ENDPOINT);
  url.searchParams.set('q', q);
  url.searchParams.set('limit', '6');
  url.searchParams.set('lang', 'en');
  url.searchParams.set('lat', String(BIAS.lat));
  url.searchParams.set('lon', String(BIAS.lon));
  url.searchParams.set('bbox', TRI_STATE_BBOX.join(','));

  try {
    const res = await fetch(url.toString(), { signal });
    if (!res.ok) return [];

    const body = (await res.json()) as { features?: PhotonFeature[] };
    const out: PlaceSuggestion[] = [];
    const seen = new Set<string>();

    for (const feature of body.features ?? []) {
      const p = feature.properties ?? {};
      const coordinates = feature.geometry?.coordinates;
      const label = labelFor(p);
      if (!label || !coordinates) continue;

      const detail = detailFor(p);
      // Photon happily returns the same address twice under different OSM ids.
      const key = `${label}|${detail}`;
      if (seen.has(key)) continue;
      seen.add(key);

      // GeoJSON is [lon, lat]; everything in this app is [lat, lng].
      out.push({ label, detail, coords: [coordinates[1], coordinates[0]] });
    }

    return out;
  } catch {
    // Offline, blocked, or aborted. The field still takes free text.
    return [];
  }
}

/** The one-line venue string stored on the event. */
export const suggestionVenue = (s: PlaceSuggestion): string =>
  s.detail ? `${s.label}, ${s.detail}` : s.label;
